import React from "react";
import { Clock, Zap, X, AlertTriangle } from "lucide-react";

/**
 * Trial status banner — shows on dashboard for free-trial users.
 * Hidden once user is on a paid plan.
 */
const TrialBanner = ({ trial, onUpgrade }) => {
  if (!trial || trial.paid) return null;

  const expired = trial.expired;
  const days = trial.days_remaining ?? 0;
  const dmsLeft = trial.dms_remaining ?? trial.dm_cap;
  const blogsLeft = trial.blogs_remaining ?? trial.blog_cap;
  const urgent = !expired && (days <= 3 || dmsLeft <= 5 || blogsLeft === 0);

  const tone = expired
    ? {
        bg: "from-red-500/15 via-[#0A192F] to-[#0A192F]",
        border: "border-red-400/40",
        accent: "text-red-300",
        dot: "bg-red-400",
      }
    : urgent
      ? {
          bg: "from-amber-500/15 via-[#0A192F] to-[#0A192F]",
          border: "border-amber-400/40",
          accent: "text-amber-200",
          dot: "bg-amber-400",
        }
      : {
          bg: "from-cyan-500/10 via-[#0A192F] to-[#0A192F]",
          border: "border-cyan-400/30",
          accent: "text-cyan-300",
          dot: "bg-cyan-400",
        };

  const title = expired
    ? "Your free trial has ended."
    : urgent
      ? "Trial running low — keep your momentum."
      : `Free Empire trial · ${days} day${days === 1 ? "" : "s"} left.`;

  const sub = expired
    ? "Upgrade now to keep shipping. No interruption to your work."
    : "No card on file. Upgrade any time — your work stays.";

  return (
    <div
      data-testid="trial-banner"
      className={`bg-gradient-to-r ${tone.bg} border ${tone.border} rounded-sm p-5 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4`}
    >
      <div className="flex items-start gap-4">
        <div className="relative">
          <Clock size={20} className={tone.accent} />
          {!expired && (
            <span
              className={`absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full ${tone.dot} animate-pulse`}
            />
          )}
        </div>
        <div>
          <p className={`font-heading font-bold text-white text-lg`} data-testid="trial-banner-title">
            {title}
          </p>
          <p className="text-sm text-slate-300 mt-1">{sub}</p>

          {!expired && (
            <div className="mt-3 flex flex-wrap gap-2">
              <span
                className="inline-flex items-center gap-2 px-2.5 py-1 bg-[#050B14] border border-white/10 rounded-sm font-mono text-[11px]"
                data-testid="trial-quota-dms"
              >
                <Zap size={11} className="text-cyan-300" />
                <span className="text-white">{dmsLeft}</span>
                <span className="text-slate-500 uppercase tracking-widest text-[9px]">
                  / {trial.dm_cap} DMs left
                </span>
              </span>
              <span
                className="inline-flex items-center gap-2 px-2.5 py-1 bg-[#050B14] border border-white/10 rounded-sm font-mono text-[11px]"
                data-testid="trial-quota-blogs"
              >
                <Zap size={11} className="text-cyan-300" />
                <span className="text-white">{blogsLeft}</span>
                <span className="text-slate-500 uppercase tracking-widest text-[9px]">
                  / {trial.blog_cap} blogs left
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {expired && <AlertTriangle size={16} className="text-red-300" />}
        <button
          onClick={onUpgrade}
          data-testid="trial-banner-upgrade"
          className="px-4 py-2 bg-[#00F0FF] text-[#050B14] font-bold text-xs uppercase tracking-widest rounded-sm hover:bg-[#33F3FF]"
        >
          {expired ? "Upgrade Now" : "See Plans"}
        </button>
      </div>
    </div>
  );
};

export default TrialBanner;
